import type { IconButtonProps } from '@mui/material/IconButton';
import type { LangCode } from 'src/locales';

import { m } from 'framer-motion';
import { useMemo, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';

import { PUBLIC_PATH } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { allLangs, useTranslate } from 'src/locales';
import {
  getPoopDataLocaleFromPathname,
  getPoopDataLanguageSwitchPath,
} from 'src/pages/poop-data/seo';

import { FlagIcon } from 'src/components/flag-icon';
import { CustomPopover } from 'src/components/custom-popover';
import { varTap, varHover, transitionTap } from 'src/components/animate';

// ----------------------------------------------------------------------

export type LanguagePopoverProps = IconButtonProps;

const isTermsPath = (pathname: string) => pathname === PUBLIC_PATH.terms.root || pathname.startsWith(`${PUBLIC_PATH.terms.root}/`);

const normalizeLocaleQuery = (locale?: string | null): LangCode | undefined => {
  const languageOnly = locale?.split(/[-_]/)[0]?.toLowerCase();

  return allLangs.find((lang) => lang.value === languageOnly)?.value;
};

const getTermsLanguageSwitchPath = (
  pathname: string,
  searchParams: URLSearchParams,
  lang: LangCode
) => {
  if (!isTermsPath(pathname)) {
    return null;
  }

  const updatedSearchParams = new URLSearchParams(searchParams);
  updatedSearchParams.set('locale', lang);

  return `${pathname}?${updatedSearchParams.toString()}`;
};

export function LanguagePopover({ sx, ...other }: LanguagePopoverProps) {
  const { open, anchorEl, onClose, onOpen } = usePopover();

  const { onChangeLang, currentLang } = useTranslate();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const selectedLang = useMemo(() => {
    const poopDataPathLang = getPoopDataLocaleFromPathname(pathname);
    const localeQueryLang = isTermsPath(pathname)
      ? normalizeLocaleQuery(searchParams.get('locale'))
      : undefined;
    const pathLang = poopDataPathLang ?? localeQueryLang;

    return allLangs.find((lang) => lang.value === pathLang) ?? currentLang;
  }, [currentLang, pathname, searchParams]);

  const handleChangeLang = useCallback(
    async (lang: LangCode) => {
      const poopDataPath = getPoopDataLanguageSwitchPath(pathname, lang);
      const termsPath = getTermsLanguageSwitchPath(pathname, searchParams, lang);

      await onChangeLang(lang);

      if (poopDataPath) {
        router.replace(poopDataPath);
      } else if (termsPath) {
        router.replace(termsPath);
      }

      onClose();
    },
    [onChangeLang, onClose, pathname, router, searchParams]
  );

  const renderMenuList = () => (
    <CustomPopover open={open} anchorEl={anchorEl} onClose={onClose}>
      <MenuList sx={{ width: 160, minHeight: 72 }}>
        {allLangs.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedLang.value}
            onClick={() => handleChangeLang(option.value)}
          >
            <FlagIcon code={option.countryCode} />
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Languages button"
        onClick={onOpen}
        sx={[
          (theme) => ({
            p: 0,
            width: 40,
            height: 40,
            ...(open && { bgcolor: theme.vars.palette.action.selected }),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <FlagIcon code={selectedLang.countryCode} />
      </IconButton>

      {renderMenuList()}
    </>
  );
}
